import { useRouter } from 'next/router';
import Loader from '../common/loader/Loader';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import { useEffect, useState } from 'react';
import PageLayout from '../common/page-layout/PageLayout';
import SelectedDeckHeader from '../common/selected-deck-header/SelectedDeckHeader';
import { DeckPages } from '../common/selected-deck-header/models';
import { useAuth } from '@/app/context/AuthContext/useAuth';
import ReactMarkdown from 'react-markdown';
import { useFeedback } from '@/app/context/FeedbackContext/useFeedback';
import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import Link from 'next/link';
import { renderToString } from 'react-dom/server';
import StudyGuideAPI from '@/app/api/StudyGuideAPI/StudyGuideAPI';
import htmlDocx from 'html-docx-js/dist/html-docx';
import { saveAs } from 'file-saver';
import { base64toBlob } from '@/app/utils/stringUtils';
import Image from 'next/legacy/image';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { ProFeatureKey } from '../common/modal/upgrade/Upgrade';
import { useModal } from '@/app/context/ModalContext/useModal';
import ConvertAPI from '@/app/api/ConvertAPI/ConvertAPI';
import { StudyGuideCustomizer } from './study-guide-customizer/StudyGuideCustomizer';
import { StudyGuideOptions } from '@/app/api/StudyGuideAPI/StudyGuideAPI';

export const StudyGuideViewer = () => {
  const router = useRouter();

  const { trackEvent, analyticsInstance } = useAnalytics();

  const { isPro, isProEligible } = useCustomer();

  const { openModal } = useModal();

  const [showFailure, setShowFailure] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { submitFeedback } = useFeedback();

  const [isLoading, setIsLoading] = useState(false);
  const [studyGuideMarkdown, setStudyGuideMarkdown] = useState('');
  const [didTrackUpgrade, setDidTrackUpgrade] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isCustomGeneration, setIsCustomGeneration] = useState(false);

  const { getStudyGuide, selectedDeck } = useDeck();

  const loadStudyGuide = async (deckId: string, options?: StudyGuideOptions) => {
    setIsLoading(true);
    setShowCustomizer(false);
    setIsCustomGeneration(!!options);
    try {
      let markdown: string;
      
      if (options) {
        // Use custom study guide API with options
        markdown = await StudyGuideAPI.getCustomStudyGuide(deckId, options);
        trackEvent(analyticsInstance, 'custom_study_guide_loaded', {
          deck_id: selectedDeck.id,
          content_source: options.contentSource,
          has_custom_prompt: options.customPrompt.length > 0,
          suggestions_count: options.selectedSuggestions.length
        });
      } else {
        // Use default study guide API
        markdown = await getStudyGuide(deckId);
        trackEvent(analyticsInstance, 'study_guide_loaded', {
          deck_id: selectedDeck.id
        });
      }
      
      setStudyGuideMarkdown(markdown);
    } catch (e) {
      try {
        trackEvent(analyticsInstance, 'study_guide_failed', {
          error: e.toString(),
          deck_id: selectedDeck.id
        });
        submitFeedback(
          'Study Guide Failed to Load:' +
            (e ? e.toString() : 'unknown exception')
        );
      } catch {}

      setShowFailure(true);
    }

    setIsLoading(false);
    setIsCustomGeneration(false);
  };

  const loadSuggestions = async (contentSource: 'all' | 'flagged' = 'all') => {
    try {
      const deckSuggestions = await StudyGuideAPI.getSuggestions(selectedDeck.id, contentSource);
      setSuggestions(deckSuggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      // Fallback suggestions
      const fallbackSuggestions = [
        "Key Concepts and Definitions",
        "Main Topics and Themes",
        "Important Formulas and Methods",
        "Core Principles and Rules",
        "Essential Facts and Details",
        "Critical Processes and Steps"
      ];
      setSuggestions(fallbackSuggestions);
    }
  };

  const handleCustomizerGenerate = (options: StudyGuideOptions) => {
    loadStudyGuide(selectedDeck.id, options);
  };


  const handleCustomizerCancel = () => {
    setShowCustomizer(false);
  };

  const showCustomizeView = () => {
    setSuggestions([]); // Clear existing suggestions immediately to show loading state
    setShowCustomizer(true);
    loadSuggestions('all');
  };

  useEffect(() => {
    if (!isPro && isProEligible && studyGuideMarkdown && !didTrackUpgrade) {
      setDidTrackUpgrade(true);
      trackEvent(analyticsInstance, 'upgrade_visible', {
        upgrade_source: 'study_guide'
      });
    }
  }, [isPro, isProEligible, studyGuideMarkdown]);

  useEffect(() => {
    if (selectedDeck?.id && !isLoading && !studyGuideMarkdown) {
      // Load default study guide on initial load only if not already loaded
      loadStudyGuide(selectedDeck.id);
    }
  }, [selectedDeck.id]);

  let body = <></>;

  if (showFailure) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex items-center text-lg font-semibold text-secondary-content">
            <div className="ml-2 text-center">
              <div>
                Study Guide failed to load.
                <br />
                <br />
                <b>
                  Please try again in a few minutes or check back later.
                </b>{' '}
                <br />
                <br />
                We will investigate the cause of this issue.
                <br />
                <br />
                <button
                  onClick={showCustomizeView}
                  className="btn btn-primary"
                >
                  Create Custom Study Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (isLoading) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex items-center text-lg font-semibold text-secondary-content">
            <Loader />
            <div className="ml-2">
              <div>{isCustomGeneration ? 'Loading Customized Study Guide...' : 'Loading Study Guide...'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!isPro) {
    const splitMarkdown = studyGuideMarkdown.split('\n');

    let stopChar = studyGuideMarkdown.length / 3;
    let charsUsed = 0;
    let stopIndex = 0;

    for (const text of splitMarkdown) {
      charsUsed += text.length + 2;
      if (charsUsed > stopChar) {
        break;
      } else {
        stopIndex++;
      }
    }

    const previewSection = splitMarkdown.slice(0, stopIndex).join('\n');
    const blockedSection = splitMarkdown.slice(stopIndex).join('\n');

    body = (
      <div className="container p-8">
        <div>
          <div className="prose max-w-none prose-headings:text-base-content prose-p:text-base-content prose-a:text-base-content hover:prose-a:text-base-content prose-blockquote:border-white prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:font-light prose-blockquote:text-base-content prose-strong:text-base-content prose-em:text-base-content prose-ol:list-inside prose-ul:list-inside prose-li:text-base-content prose-li:marker:text-base-content prose-table:border prose-th:bg-white prose-th:p-2 prose-th:font-sans prose-th:text-base-content prose-td:p-2 prose-img:w-full prose-img:border prose-img:p-1">
            <ReactMarkdown>{previewSection}</ReactMarkdown>
          </div>
        </div>
        <div className="mb-8 mt-8 flex max-w-5xl flex-col items-center justify-center gap-2 rounded border-solid border-secondary bg-base-300 p-4 text-center opacity-100 shadow-xl">
          <div>
            <span
              onClick={() => {
                trackEvent(analyticsInstance, 'upgrade_click', {
                  upgrade_source: 'study_guide'
                });
                trackEvent(analyticsInstance, 'study_guide_click_upgrade', {
                  upgrade_source: 'study_guide',
                  deck_id: selectedDeck.id
                });

                openModal('upgrade', 'studyGuides' as ProFeatureKey);
              }}
              className="link font-bold"
              style={{ color: 'tomato' }}
            >
              Upgrade to Limbiks Pro
            </span>{' '}
            to unlock full study guides with every upload.
          </div>
        </div>
        <div className="pointer-events-none select-none blur">
          <div className="prose max-w-none prose-headings:text-base-content prose-p:text-base-content prose-a:text-base-content hover:prose-a:text-base-content prose-blockquote:border-white prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:font-light prose-blockquote:text-base-content prose-strong:text-base-content prose-em:text-base-content prose-ol:list-inside prose-ul:list-inside prose-li:text-base-content prose-li:marker:text-base-content prose-table:border prose-th:bg-white prose-th:p-2 prose-th:font-sans prose-th:text-base-content prose-td:p-2 prose-img:w-full prose-img:border prose-img:p-1">
            <ReactMarkdown>{blockedSection}</ReactMarkdown>
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="container p-8">
        <div className="prose max-w-none prose-headings:text-base-content prose-p:text-base-content prose-a:text-base-content hover:prose-a:text-base-content prose-blockquote:border-white prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:font-light prose-blockquote:text-base-content prose-strong:text-base-content prose-em:text-base-content prose-li:text-base-content prose-li:marker:text-base-content prose-table:border prose-th:bg-white prose-th:p-2 prose-th:font-sans prose-th:text-base-content prose-td:p-2 prose-img:w-full prose-img:border prose-img:p-1 prose-hr:bg-white">
          <ReactMarkdown>{studyGuideMarkdown}</ReactMarkdown>
        </div>
      </div>
    );
  }

  const onPrint = () => {
    trackEvent(analyticsInstance, 'study_guide_click_print', {
      deck_id: selectedDeck.id
    });

    window.frames['test_frame'].focus();
    window.frames['test_frame'].print();
  };

  const onDownload = async (isPdf: boolean) => {
    try {
      setIsDownloading(true);

      const html = renderToString(
        <ReactMarkdown>{studyGuideMarkdown}</ReactMarkdown>
      );

      if (isPdf) {
        const htmlPageContent = `
          <!DOCTYPE html>
          <html lang="en">
          <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Study Guide</title>
          </head>
          <body>
              ${html}
          </body>
          </html>
        `;

        const pdfData = await ConvertAPI.htmlToPdf(htmlPageContent);

        trackEvent(analyticsInstance, 'study_guide_click_pdf', {
          deck_id: selectedDeck.id
        });

        var blobData = new Blob([pdfData], {
          type: 'application/pdf'
        });

        saveAs(blobData, `${selectedDeck?.name.split('.')[0]} Study Guide.pdf`);
      } else {
        trackEvent(analyticsInstance, 'study_guide_click_docx', {
          deck_id: selectedDeck.id
        });

        var converted = htmlDocx.asBlob(html);

        saveAs(
          converted,
          `${selectedDeck?.name.split('.')[0]} Study Guide.docx`
        );
      }
    } catch (e) {
      submitFeedback('Failed to download Study guide: ' + e.toString());
      console.log(e.toString());
      window.alert(
        'Failed to download study guide. This issue will be investigated. Please try again later.'
      );
    }
    setIsDownloading(false);
  };

  const disabled = !isPro;

  if (isLoading || showFailure) {
    return (
      <PageLayout authenticated>
        <SelectedDeckHeader
          currentPage={DeckPages.StudyGuide}
          onBack={() => {
            router.back();
          }}
        ></SelectedDeckHeader>
        {body}
      </PageLayout>
    );
  } else if (showCustomizer) {
    return (
      <PageLayout authenticated>
        <SelectedDeckHeader
          currentPage={DeckPages.StudyGuide}
          onBack={() => {
            router.back();
          }}
        ></SelectedDeckHeader>
        <div className="mt-[132px]">
          <StudyGuideCustomizer
            onGenerate={handleCustomizerGenerate}
            onCancel={handleCustomizerCancel}
            isGenerating={isLoading}
            suggestions={suggestions}
            onLoadSuggestions={loadSuggestions}
          />
        </div>
      </PageLayout>
    );
  } else {
    return (
      <PageLayout authenticated>
        <SelectedDeckHeader
          currentPage={DeckPages.StudyGuide}
          onBack={() => {
            router.back();
          }}
        ></SelectedDeckHeader>

        {studyGuideMarkdown && (
          <iframe
            style={{ display: 'none' }}
            id="test_frame"
            name="test_frame"
            srcDoc={renderToString(
              <div style={{ padding: 10 }}>
                <ReactMarkdown>{studyGuideMarkdown}</ReactMarkdown>
              </div>
            )}
          />
        )}
        {studyGuideMarkdown && (
          <div className="mt-[132px] w-full bg-accent">
            <div
              data-tip={
                'Upgrade to Limbiks Pro to print study guides or download as Word Documents and PDFs'
              }
              className={`flex flex-row-reverse items-center justify-center md:float-right md:inline-block ${
                isPro ? '' : 'tooltip tooltip-left'
              } `}
            >
              {!isDownloading && (
                <>
                  <div
                    onClick={showCustomizeView}
                    className="z-40 float-right my-4 mr-4 inline-flex items-center justify-center bg-primary cursor-pointer duration-300 ease-in-out hover:scale-105 hover:underline transform rounded px-4 py-2 font-bold text-primary-content transition"
                  >
                    <span className="text-sm">Customize</span>
                  </div>
                  <div
                    onClick={() => (!disabled ? onPrint() : null)}
                    data-tip="Print"
                    className={`z-30 float-right my-4 mr-4 inline-flex items-center justify-center bg-neutral ${
                      !disabled
                        ? 'tooltip tooltip-left cursor-pointer duration-300 ease-in-out hover:scale-105 hover:underline'
                        : 'opacity-60'
                    } transform rounded px-4 py-2 font-bold text-neutral-content transition`}
                  >
                    <div className="h-5 w-5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height={24}
                        width={24}
                        viewBox="0 0 24 24"
                        className="fill-base-content"
                      >
                        <path d="M7 17H17V22H7V17ZM19 20V15H5V20H3C2.44772 20 2 19.5523 2 19V9C2 8.44772 2.44772 8 3 8H21C21.5523 8 22 8.44772 22 9V19C22 19.5523 21.5523 20 21 20H19ZM5 10V12H8V10H5ZM7 2H17C17.5523 2 18 2.44772 18 3V6H6V3C6 2.44772 6.44772 2 7 2Z"></path>
                      </svg>
                    </div>
                  </div>
                  <div
                    onClick={() => (!disabled ? onDownload(true) : null)}
                    data-tip="Download PDF"
                    className={`z-20 float-right m-4 inline-flex items-center justify-center bg-neutral ${
                      !disabled
                        ? 'tooltip tooltip-left cursor-pointer duration-300 ease-in-out hover:scale-105 hover:underline'
                        : 'opacity-60'
                    }  transform rounded px-4 py-2 font-bold text-neutral-content transition`}
                  >
                    <Image width={20} height={20} alt="pdf" src="/pdf.png" />
                  </div>
                  <div
                    onClick={() => (!disabled ? onDownload(false) : null)}
                    data-tip="Download Microsoft Word Document"
                    className={`float-right my-4 ml-4 inline-flex items-center justify-center bg-neutral ${
                      !disabled
                        ? 'tooltip tooltip-left cursor-pointer duration-300 ease-in-out hover:scale-105 hover:underline'
                        : 'opacity-60'
                    } transform rounded px-4 py-2 font-bold text-neutral-content transition`}
                  >
                    <Image
                      width={20}
                      height={20}
                      alt="word document"
                      src="/word_icon.svg"
                    />
                  </div>
                </>
              )}
              {isDownloading && (
                <div className="m-4 flex items-center text-lg font-semibold text-secondary-content">
                  <Loader />
                  <div className="ml-2">
                    <div>Downloading...</div>
                  </div>
                </div>
              )}
            </div>

            {body}
          </div>
        )}
      </PageLayout>
    );
  }
};
